import type { AxiosError } from 'axios';
import { makeAutoObservable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import { FavoritesRequest } from '~/api/Favorites';
import type { ApiErrorResponse } from '~/api/Requests';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';
import { cartStore, mainStore, orderStore, userStore } from '~/stores';
import { catalogWarehouseParam } from '~/utils/catalogWarehouse';

import { Product } from '../CategoriesStore';
import type { BaseStore } from '../interfaces/BaseStore';
import { storage as localStorage } from '../LocalStorage';
import { MetaStore } from '../shared/MetaStore';
import type { Pagination } from './interfaces';

export class FavoritesStore implements BaseStore {
  private readonly _abortController: AbortController = new AbortController();

  readonly favoritesStoreCRUDMeta: MetaStore = new MetaStore();

  favorites: Product[] = [];
  favoritesIds: number[] = [];
  isCanFetchFavouritesMore: boolean = true;

  readonly favoritesPagination: Pagination = {
    current: 0,
    size: 20,
  };

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
    makePersistable(this, {
      name: 'FavoritesStore',
      properties: ['favoritesIds'],
      storage: localStorage,
    }).catch((error) => error && console.error(error));
  }

  setFavorites(obj: Product[]) {
    this.favorites.push(...obj);
  }

  setFavoritesIds(obj: number[]) {
    this.favoritesIds = obj;
  }

  setIsCanFetchFavouritesMore(value: boolean) {
    this.isCanFetchFavouritesMore = value;
  }

  async fetchFavorites(isForce?: boolean) {
    if (!userStore.isAuthorized || this.favoritesStoreCRUDMeta.isLoading) {
      return;
    }

    if (isForce) {
      this.favoritesPagination.current = 1;
      this.favorites = [];
    } else {
      this.favoritesPagination.current += 1;
    }

    this.favoritesStoreCRUDMeta.setLoading(true);

    try {
      const { data } = await FavoritesRequest.getFavorites({
        ...catalogWarehouseParam(),
        isSafeRequest: mainStore.isSafeRequest || undefined,
        page: this.favoritesPagination,
      });
      if (!data?.length) {
        return false;
      }
      const forEntries = data.map((product) => new Product(product));
      this.setFavorites(forEntries);

      const loadedIds = forEntries.map((product) => product.id);
      this.setFavoritesIds([...new Set([...this.favoritesIds, ...loadedIds])]);

      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'fetchFavorites',
      );
    } finally {
      this.favoritesStoreCRUDMeta.setLoading(false);
    }
  }

  async fetchFavoritesIds() {
    if (!userStore.isAuthorized) {
      return;
    }

    try {
      const { data } = await FavoritesRequest.getFavoritesIds();
      if (!data?.length) {
        return false;
      }

      this.setFavoritesIds(data);
      return true;
    } catch (e) {
      await this.errorHandler(
        e as AxiosError<ApiErrorResponse>,
        'fetchFavoritesIds',
      );
    }
  }

  addFavorite(ids: number[]) {
    FavoritesRequest.addManyFavorites({ ids }).catch((e) => {
      this.errorHandler(e, 'addFavorite').catch(
        (error) => error && console.error(error),
      );
    });
  }

  removeFavorite(id: number) {
    FavoritesRequest.removeFavorite(id).catch((e) => {
      this.errorHandler(e, 'removeFavorite').catch(
        (error) => error && console.error(error),
      );
    });
  }

  toggleFavorite(product: Product, source: string) {
    const isCurrentlyFavorited = this.favoritesIds.includes(product.id);

    if (isCurrentlyFavorited) {
      this.setFavoritesIds(this.favoritesIds.filter((id) => id !== product.id));
      this.favorites = this.favorites.filter((fav) => fav.id !== product.id);

      if (userStore.isAuthorized) {
        this.removeFavorite(product.id);
      }
    } else {
      this.setFavoritesIds([...this.favoritesIds, product.id]);
      this.favorites = [...this.favorites, product];

      if (userStore.isAuthorized) {
        this.addFavorite([product.id]);
      }

      this.trackFavoriteAdded(product, source);
    }

    mainStore.sendToRN('setUserProperties', {
      'Commerce: favourites number': Object.keys(this.favorites).length,
    });
  }

  /**
   * Reports a product that has just been added to favorites. Lives outside
   * `toggleFavorite` so that removing a favorite cannot report an addition —
   * both events count additions, and BI/Firebase were double-counting them.
   */
  private trackFavoriteAdded(product: Product, source: string) {
    analyticsService.track(AnalyticsEvent.PRODUCT_ADDED_TO_FAVORITES, {
      product_id: product.id,
      category_id: product.categoryId,
      lvl1_category_id: undefined,
      lvl2_category_id: product.parentCategoryId,
      source,
      price: product.priceFormatted,
      final_price: product.discountPrice
        ? product.discountPriceFormatted
        : product.priceFormatted,
    });
    analyticsService.track(AnalyticsEvent.ADD_TO_WISHLIST, {
      currency: orderStore.currency.toUpperCase(),
      items: [
        {
          item_id: product.id,
          item_name: product.name,
          quantity: 1,
          promotion_id: '',
          promotion_name: '',
          affiliation: '',
          coupon: cartStore.promocode.value,
          creative_name: '',
          creative_slot: '',
          discount: product.discountPrice
            ? mainStore.toFloat(
                mainStore.convertPenceToPounds(
                  product.discountPrice - product.price,
                ),
              )
            : 0,
          index: 0,
          item_brand: '',
          item_category: product.categoryName,
          item_list_name: product.categoryName,
          item_list_id: product.categoryId,
          item_variant: '',
          location_id: '',
          tax: 0,
          price: mainStore.toFloat(product.priceFormatted),
          currency: orderStore.currency.toUpperCase(),
        },
      ],
      value: mainStore.toFloat(product.priceFormatted),
    });
  }

  isFavorited(productId: number | undefined): boolean {
    if (!productId) {
      return false;
    }
    return this.favoritesIds.includes(productId);
  }

  reset() {
    this.favorites = [];
    this.favoritesIds = [];
    this.isCanFetchFavouritesMore = true;
    this.favoritesPagination.current = 0;
  }

  resetFavoritesList() {
    this.favorites = [];
    this.isCanFetchFavouritesMore = true;
    this.favoritesPagination.current = 0;
  }

  errorHandler = (
    error: AxiosError<ApiErrorResponse>,
    context: string,
  ): Promise<AxiosError> => mainStore.errorHandler(error, context);

  destroy() {
    this.favoritesStoreCRUDMeta.destroy();
    this._abortController.abort();
  }
}

export const favoritesStore = new FavoritesStore();
